import { TelegramCategoryDynamicParameterResponseDto } from '@/generated';
import { CommonParams } from '@/utils/SEOPathModule/convertParamsToPath';
import { ParamItem } from '@/utils/SEOPathModule/convertPathToParams';

import { Dicts, ExtendedCategoryParamsDict } from '@/utils/SEOPathModule/types';
import { convertCategoryParametersToFlat, isObjValueExists } from '@/utils/SEOPathModule/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OnChangeIsFreeType, OnChangePriceType, OnUpdateParamType, PriceValue, ShownParams } from './types';
import { useGetCategoryIdDict } from '@/hooks/useGetCategoryIdDict';

type State = {
  params: ParamItem[];
  commonParams: CommonParams;
  categories: string[];
};
type Props = {
  initialValue: State;
  initialDicts: Pick<Dicts, 'categoryIdDict'>;
};

const emptyArr = [];

const SCOPE_KEY = 'Search';

const isRangeParamValue = (
  value: ParamItem['value'] | undefined,
): value is Extract<ParamItem['value'], { from?: number; to?: number }> => {
  return value !== undefined && !Array.isArray(value) && typeof value === 'object';
};

export const useFiltersLocalState = ({ initialValue, initialDicts }: Props) => {
  const [filtersLocalValue, setFiltersLocalValue] = useState(initialValue);
  const { data: categoryIdDictLoaded, isLoading } = useGetCategoryIdDict();

  useEffect(() => {
    setFiltersLocalValue(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryIdDict = useMemo(() => {
    if (Object.keys(categoryIdDictLoaded).length > 0) {
      return categoryIdDictLoaded;
    }
    return initialDicts.categoryIdDict;
  }, [categoryIdDictLoaded, initialDicts.categoryIdDict]);

  const [shownParameters, setShownParameters] = useState<ShownParams>(emptyArr);

  const lastCategoryId = filtersLocalValue.categories[filtersLocalValue.categories.length - 1];

  const paramsDict = useMemo(() => {
    const lastCategory = categoryIdDict[lastCategoryId];

    return convertCategoryParametersToFlat(lastCategory?.parameters || []);
  }, [lastCategoryId, categoryIdDict]);

  useEffect(() => {
    const newLastCategory = categoryIdDict[lastCategoryId];
    const newParams: ShownParams = Array.from(newLastCategory?.parameters || emptyArr);
    let allDynamicParams: TelegramCategoryDynamicParameterResponseDto[] = emptyArr;

    newParams.forEach((newParam) => {
      if ('children' in newParam && newParam.children) {
        allDynamicParams = [...allDynamicParams, ...newParam.children];
      }
    });
    if (newParams.length === 0 && allDynamicParams.length === 0) {
      setShownParameters([]);
      return;
    }
    let hasRun = false;
    setFiltersLocalValue((local) => {
      const localParams = local.params;
      const newParamsValues: any[] = [];
      localParams.forEach((paramValue) => {
        const foundSameParam = newParams.find((val) => val.id === paramValue.id);
        const dynamicParamInValues = allDynamicParams.find(
          (el) => typeof paramValue.value === 'string' && isObjValueExists(el.dynamicVariant, paramValue.value),
        );
        if (dynamicParamInValues) {
          if (!hasRun) newParams.push(dynamicParamInValues);
          newParamsValues.push(paramValue);
          return;
        }
        if (foundSameParam) {
          newParamsValues.push(paramValue);
          return;
        }

        // we found existing dynamic param
        // we should keep it
      });
      setShownParameters(newParams.filter((x) => x.scopes.includes(SCOPE_KEY)));
      hasRun = true;
      return { ...local, params: newParamsValues };
    });
  }, [lastCategoryId, categoryIdDict]);

  const onUpdateCategory = useCallback(
    (newCategories: string[]) => {
      setFiltersLocalValue({
        ...filtersLocalValue,
        categories: newCategories,
        commonParams: {
          ...filtersLocalValue.commonParams,
          isFree: undefined,
        },
      });
    },
    [filtersLocalValue],
  );

  // here add dynamic param
  const onUpdateParam: OnUpdateParamType = useCallback(
    (id: string, value: ParamItem['value']) => {
      const existingParam = filtersLocalValue.params.find((p) => p.id === id);

      let resultParams: ParamItem[] = [];
      let shoulAddNewParamsIfExist = true;
      if (existingParam) {
        const isEmptyRange = isRangeParamValue(value) && value.from === undefined && value.to === undefined;

        if ((Array.isArray(value) && value.length === 0) || value === '' || value === undefined || isEmptyRange) {
          resultParams = filtersLocalValue.params.filter((p) => p.id !== id);
          shoulAddNewParamsIfExist = false;
        } else {
          resultParams = filtersLocalValue.params.map((p) => (p.id === id ? { id, value } : p));
          if (Array.isArray(value) && value.length !== 1) {
            shoulAddNewParamsIfExist = false;
          }
          if (isRangeParamValue(value)) {
            shoulAddNewParamsIfExist = false;
          }
        }

        const { params, shownParams } = convertDynamicParams(
          value,
          id,
          shoulAddNewParamsIfExist,
          paramsDict.dict,
          shownParameters,
          resultParams,
        );

        setFiltersLocalValue({
          ...filtersLocalValue,
          params: params,
        });

        setShownParameters(shownParams.filter((x) => x.scopes.includes(SCOPE_KEY)));

        return;
      }

      /**
       * the logic of dynamic params:
       * if there is new value for existing param (editing) - find its children in the shownparams and values
       * if the new value has new dynamic param - change it in shown params, remove from the params itself
       * if it arrayed, or no value, or no dynamic param - just remove the found child dynamic param both from local and from shown.
       *
       * if there is new param, and the value is new - find dynamic params that can be used here, add it to shown params.
       *
       *
       */
      resultParams = [...filtersLocalValue.params, { id, value }];

      const { params, shownParams } = convertDynamicParams(
        value,
        id,
        shoulAddNewParamsIfExist,
        paramsDict.dict,
        shownParameters,
        resultParams,
      );
      setFiltersLocalValue({
        ...filtersLocalValue,
        params,
      });

      setShownParameters(shownParams.filter((x) => x.scopes.includes(SCOPE_KEY)));
    },
    [filtersLocalValue, paramsDict.dict, shownParameters],
  );

  const priceValue: PriceValue = useMemo(() => {
    return {
      max: filtersLocalValue.commonParams.maxPrice ? Number(filtersLocalValue.commonParams.maxPrice) : undefined,
      min: filtersLocalValue.commonParams.minPrice ? Number(filtersLocalValue.commonParams.minPrice) : undefined,
    };
  }, [filtersLocalValue]);

  const onChangePrice: OnChangePriceType = useCallback(
    ({ min, max }: any) => {
      setFiltersLocalValue({
        ...filtersLocalValue,
        commonParams: {
          ...filtersLocalValue.commonParams,
          minPrice: min !== undefined ? String(min) : '',
          maxPrice: max !== undefined ? String(max) : '',
        },
      });
    },
    [filtersLocalValue],
  );

  const onChangeIsFree: OnChangeIsFreeType = useCallback(
    (newVal: boolean) => {
      setFiltersLocalValue({
        ...filtersLocalValue,
        commonParams: {
          ...filtersLocalValue.commonParams,
          isFree: newVal,
          minPrice: undefined,
          maxPrice: undefined,
        },
      });
    },
    [filtersLocalValue],
  );

  const onChangeSearch = useCallback(
    (newVal: string) => {
      setFiltersLocalValue({
        ...filtersLocalValue,
        commonParams: {
          ...filtersLocalValue.commonParams,
          search: newVal,
        },
      });
    },
    [filtersLocalValue],
  );

  const onChangeSort = useCallback(
    (newVal: string) => {
      setFiltersLocalValue({
        ...filtersLocalValue,
        commonParams: {
          ...filtersLocalValue.commonParams,
          sortBy: newVal,
        },
      });
    },
    [filtersLocalValue],
  );

  const onCancel = useCallback(() => {
    setFiltersLocalValue({
      ...filtersLocalValue,
      commonParams: {
        ...filtersLocalValue.commonParams,
        minPrice: undefined,
        maxPrice: undefined,
        isFree: undefined,
      },
      params: [],
    });
  }, [filtersLocalValue]);

  const onDropParamsSearchChanged = useCallback(() => {
    setFiltersLocalValue({
      categories: [],
      commonParams: {
        ...filtersLocalValue.commonParams,
        minPrice: '',
        maxPrice: '',
        isFree: undefined,
      },
      params: [],
    });
  }, [filtersLocalValue]);

  const isFreeCategory = lastCategoryId ? categoryIdDict[lastCategoryId]?.priceSettings?.allowFreePrice || false : true;
  return {
    shownParameters: shownParameters.sort((a, b) => a.order - b.order),
    priceValue,
    onChangePrice,
    onChangeIsFree,
    onUpdateParam,
    onUpdateCategory,
    onChangeSearch,
    isLoading,
    filtersLocalValue,
    onCancel,
    searchValue: filtersLocalValue.commonParams.search,
    showFree: isFreeCategory,
    isFree: filtersLocalValue.commonParams.isFree === true,
    onDropParamsSearchChanged,
    onChangeSort,
  };
};

const convertDynamicParams = (
  value: ParamItem['value'],
  id: string,
  shoulAddNewParamsIfExist = false,
  paramsDict: ExtendedCategoryParamsDict,
  shownParams: ShownParams,
  params: ParamItem[],
): { shownParams: ShownParams; params: ParamItem[] } => {
  const resValue = Array.isArray(value)
    ? shoulAddNewParamsIfExist
      ? value[0]
      : undefined
    : typeof value === 'string'
      ? value
      : undefined;

  const dependantParamsIds: string[] = [];
  const addedParam = paramsDict[id];
  const newParamsToAdd: TelegramCategoryDynamicParameterResponseDto[] = [];
  if ('children' in addedParam) {
    addedParam.children.forEach((el) => {
      dependantParamsIds.push(el.id);

      if (shoulAddNewParamsIfExist && resValue && isObjValueExists(el.dynamicVariant, resValue)) {
        newParamsToAdd.push(el);
      }
    });
  }

  const resultParams = [
    ...params.filter((val) => !dependantParamsIds.includes(val.id)),
    ...newParamsToAdd.map((val) => ({ id: val.id, value: val.isMultiselectable ? [] : '' })),
  ];
  const newShown = [
    ...shownParams.filter((val) => !dependantParamsIds.includes(val.id)),
    ...newParamsToAdd.map((val) => val),
  ];

  return {
    shownParams: newShown,
    params: resultParams,
  };
};
